import "./RetailOpsChannels.scss";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as R from "ramda";
import {
  Button,
  ButtonType,
  Dropdown,
  FullPageSpinner,
  InfoTooltip,
  Input,
  Page,
  Spinner,
} from "../Components";
import RetailOpsForm from "./RetailOpsForm";
import { useCompanyInfo } from "../redux/company";
import { BPMTable } from "../Components/StickyTable/BPMTable";
import { AmazonLambdaFetch, awaitJSON } from "../utils/fetch-utils";
import { useSetError } from "../redux/modals";
import { COUNTRY_TO_MARKETPLACE_ID } from "@blisspointmedia/bpm-types/dist/AmazonSPAPI";

const RetailOpsChannels: React.FC = () => {
  const setError = useSetError();
  const companyInfo = useCompanyInfo();
  const { mobiusx_account_id: accountId, cid: company } = companyInfo;
  const [accountRows, setAccountRows] = useState<any[] | null>(null);
  const [channelType, setChannelType] = useState<string>("");
  const [applicationType, setApplicationType] = useState("");
  const [brand, setBrand] = useState("");
  const [marketplace, setMarketplace] = useState("");
  const [loading, setLoading] = useState<boolean>(false);

  const [redirectUrl, setRedirectUrl] = useState<string>("");

  const checkAuthorization = useCallback(async () => {
    try {
      const res = await AmazonLambdaFetch("/checkSellingPartnerAuthorization", {
        params: { company, applicationType, marketplace, brand },
      });
      const data = await awaitJSON<string>(res);
      if (data.substring(0, 5) === "https") {
        setRedirectUrl(data);
      } else {
        setError({
          variant: "success",
          message: data,
        });
      }
    } catch (e) {
      const reportError = e as Error;
      setError({
        message: `Failed to check SPAPI authorization: ${reportError.message}`,
        reportError,
      });
    }
  }, [company, applicationType, marketplace, brand, setError]);

  const signInToAmazon = useCallback(() => {
    (() => {
      try {
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      } catch (e) {
        const reportError = e as Error;
        setError({
          message: `Failed redirect to Amazon: ${reportError.message}`,
          reportError,
        });
      }
    })();
  }, [redirectUrl, setError]);

  const oauthProcess = useCallback(async () => {
    setLoading(true);
    await checkAuthorization();
    setLoading(false);
  }, [checkAuthorization]);

  useEffect(() => {
    if (redirectUrl) {
      signInToAmazon();
    }
  }, [redirectUrl, signInToAmazon]);

  useEffect(() => {
    (async () => {
      try {
        if (!accountRows) {
          const res = await AmazonLambdaFetch("/getSellingPartnerAccounts", {
            params: { company },
          });
          const data = await awaitJSON<any[]>(res);
          setAccountRows(data);
        }
      } catch (e) {
        const reportError = e as Error;
        setError({
          message: `Failed to fetch selling partner accounts: ${reportError.message}`,
          reportError,
        });
      }
    })();
  }, [company, applicationType, accountRows, setError]);

  const TABLE_HEADERS = [
    {
      label: "Account Type",
      name: "account_type",
      width: 120,
    },
    {
      label: "Account ID",
      name: "account_id",
      width: 120,
    },
    {
      label: "Account Name",
      name: "account_name",
      flex: 1,
    },
    {
      label: "Secret Path",
      name: "secret_path",
      flex: 1,
    },
    {
      label: "Marketplace ID",
      name: "marketplace_id",
      width: 200,
    },
    {
      label: "Marketplace",
      name: "marketplace",
      width: 220,
    },
  ];

  const marketplaceOptions = useMemo(() => {
    return Object.keys(COUNTRY_TO_MARKETPLACE_ID).map(key => ({
      label: key,
      value: COUNTRY_TO_MARKETPLACE_ID[key],
    }));
  }, []);

  return (
    <Page minHeight="600px" pageType="Retail Ops Channels" title="Retail Ops Channels">
      <div className="retail-ops-channel-page">
        <div className="retailOpsControls">
          <div className="amazonOAuthContainer">
            <div className="label">Amazon SPAPI OAuth</div>
            <div className="controls">
              <Dropdown
                onChange={option => setApplicationType(option)}
                options={[
                  { label: "Amazon Selling Partner - Vendor", value: "vendor" },
                  { label: "Amazon Selling Partner - Seller", value: "seller" },
                ]}
                placeholder="Selling Partner Type"
                value={applicationType}
              />
              <Dropdown
                onChange={option => setMarketplace(option)}
                options={marketplaceOptions}
                placeholder="Marketplace"
                value={marketplace}
              />
              <div className="brandInput">
                <InfoTooltip>
                  <div>
                    <p>
                      If applicable, this brand identifier helps distinguish multiple SPAPI
                      authorizations for the same company + selling partner type + region
                      combination.
                    </p>
                    <p>
                      If not relevant, just use the company ID, which is the same as the company
                      name used in the URL.
                    </p>
                  </div>
                </InfoTooltip>
                <Input
                  onChange={e => setBrand(e.target.value)}
                  onBlur={() => setBrand(curr => curr.trim().toLocaleLowerCase().replace(/ /g, ""))}
                  value={brand}
                  placeholder="Brand"
                />
              </div>

              <Button
                type={ButtonType.FILLED}
                onClick={oauthProcess}
                disabled={!applicationType || !marketplace || !brand}
              >
                {loading ? <Spinner /> : "Submit"}
              </Button>
            </div>
          </div>
        </div>
        {channelType && (
          <RetailOpsForm
            accountId={accountId}
            setChannelType={setChannelType}
            title={channelType}
          />
        )}

        <div className="basic-card">
          {R.isNil(accountRows) ? (
            <FullPageSpinner />
          ) : (
            <div className="table-wrapper" style={{ height: "650px" }}>
              <BPMTable
                data={accountRows || []}
                filterBar={false}
                headers={TABLE_HEADERS}
                noRowsRenderer={() => (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      fontSize: "24px",
                    }}
                  >
                    There are no existing auth connections for this client. Add one by submitting
                    and authenticating above.
                  </div>
                )}
              />
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};

export default RetailOpsChannels;
